/**
 * Apple Music Service
 *
 * Handles Apple Music API integration for the Pipelines feature.
 * Uses MusicKit JS for authorization and Apple Music API for data access.
 */

import { config } from '../config';
import { iTrack } from '../types/ITrack';

// MusicKit types
declare global {
  interface Window {
    MusicKit: any;
  }
}

interface AppleMusicPlaylist {
  id: string;
  name: string;
  description?: string;
  trackCount: number;
  image?: string;
}

class AppleMusicServiceClass {
  private static musicKit: any = null;
  private static developerToken: string | null = null;
  private static userToken: string | null = null;
  private static isInitialized = false;
  private static initPromise: Promise<void> | null = null;

  /**
   * Load MusicKit JS if not already loaded
   */
  private static async loadMusicKitJS(): Promise<void> {
    if (window.MusicKit) {
      return;
    }

    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://js-cdn.music.apple.com/musickit/v3/musickit.js';
      script.async = true;
      script.crossOrigin = 'anonymous';

      script.onload = () => {
        resolve();
      };

      script.onerror = () => {
        reject(new Error('Failed to load MusicKit JS'));
      };

      document.head.appendChild(script);
    });
  }

  /**
   * Get developer token from server
   */
  private static async getDeveloperToken(): Promise<string> {
    if (this.developerToken) {
      return this.developerToken;
    }

    const apiUrl = config.PIPELINES_API_URL;
    if (!apiUrl) {
      throw new Error('PIPELINES_API_URL not configured');
    }

    const response = await fetch(`${apiUrl}/api/apple-music/token`);
    if (!response.ok) {
      throw new Error('Failed to get Apple Music developer token');
    }

    const data = await response.json();
    this.developerToken = data.developerToken;

    // Store for session
    sessionStorage.setItem('apple_music_dev_token', data.developerToken);

    return data.developerToken;
  }

  /**
   * Get developer token (public method for Electron IPC auth)
   */
  public static async fetchDeveloperToken(): Promise<string> {
    return this.getDeveloperToken();
  }

  /**
   * Set user token directly (used by Electron IPC auth)
   * This also updates MusicKit's internal state so API calls work
   */
  public static async setUserToken(token: string): Promise<void> {
    this.userToken = token;
    localStorage.setItem('apple_music_user_token', token);

    // Ensure MusicKit is initialized
    await this.initialize();

    // MusicKit v3: Set the music user token directly on the instance
    if (this.musicKit) {
      // The musicUserToken property can be set to authorize the instance
      try {
        // MusicKit v3 stores auth tokens - we need to make it recognize our token
        // The most reliable way is to configure it with the user token
        if (typeof this.musicKit.setMusicUserToken === 'function') {
          await this.musicKit.setMusicUserToken(token);
        } else {
          // Fallback: store the token and make direct API calls with it
          console.log(
            '[AppleMusic] MusicKit setMusicUserToken not available, using manual token',
          );
        }
      } catch (error) {
        console.warn('[AppleMusic] Could not set MusicKit user token:', error);
      }
    }
  }

  /**
   * Initialize MusicKit
   */
  public static async initialize(): Promise<void> {
    if (this.isInitialized && this.musicKit) {
      return;
    }

    // Prevent multiple simultaneous initializations
    if (this.initPromise) {
      return this.initPromise;
    }

    this.initPromise = this._initialize();
    return this.initPromise;
  }

  private static async _initialize(): Promise<void> {
    try {
      await this.loadMusicKitJS();

      const developerToken = await this.getDeveloperToken();

      await window.MusicKit.configure({
        developerToken,
        app: {
          name: 'Crate Hackers',
          build: '1.0.0',
        },
        // Set default storefront
        storefrontId: 'us',
      });

      this.musicKit = window.MusicKit.getInstance();
      this.isInitialized = true;

      // Check if MusicKit has a valid session from cookies/storage
      if (this.musicKit?.isAuthorized) {
        this.userToken = this.musicKit.musicUserToken;
        if (this.userToken) {
          localStorage.setItem('apple_music_user_token', this.userToken);
        }
      }

      // Load stored token from localStorage if not already authorized
      if (!this.userToken) {
        const storedToken = localStorage.getItem('apple_music_user_token');
        if (storedToken) {
          console.log('[AppleMusic] Restoring token from localStorage');
          this.userToken = storedToken;
          // Try to set it on MusicKit
          if (this.musicKit) {
            try {
              if (typeof this.musicKit.setMusicUserToken === 'function') {
                await this.musicKit.setMusicUserToken(storedToken);
              }
            } catch (error) {
              console.warn(
                '[AppleMusic] Could not restore MusicKit session:',
                error,
              );
            }
          }
        }
      }

      console.log(
        '[AppleMusic] MusicKit initialized, authorized:',
        this.musicKit?.isAuthorized,
        'hasStoredToken:',
        !!this.userToken,
      );
    } catch (error) {
      console.error('[AppleMusic] Failed to initialize:', error);
      this.initPromise = null;
      throw error;
    }
  }

  /**
   * Check if user is authorized
   */
  public static isAuthorized(): boolean {
    // Check in-memory token, MusicKit auth status, or stored token
    return (
      !!this.userToken ||
      this.musicKit?.isAuthorized ||
      !!localStorage.getItem('apple_music_user_token')
    );
  }

  /**
   * Validate the current token by making a simple API call
   * Returns true if token is valid, false if expired/invalid
   */
  public static async validateToken(): Promise<boolean> {
    const userToken =
      this.userToken || localStorage.getItem('apple_music_user_token');
    const devToken =
      this.developerToken || sessionStorage.getItem('apple_music_dev_token');

    if (!userToken || !devToken) {
      return false;
    }

    try {
      // Make a simple API call to check if token works
      const response = await fetch(
        'https://api.music.apple.com/v1/me/library/playlists?limit=1',
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${devToken}`,
            'Music-User-Token': userToken,
          },
        },
      );

      if (response.status === 403 || response.status === 401) {
        console.log(
          '[AppleMusic] Token validation failed - token expired or invalid',
        );
        return false;
      }

      return response.ok;
    } catch (error) {
      console.error('[AppleMusic] Token validation error:', error);
      return false;
    }
  }

  /**
   * Clear stored tokens and force re-authentication
   */
  public static clearTokens(): void {
    console.log('[AppleMusic] Clearing stored tokens');
    this.userToken = null;
    this.developerToken = null;
    localStorage.removeItem('apple_music_user_token');
    sessionStorage.removeItem('apple_music_dev_token');

    // Also reset MusicKit if possible
    if (this.musicKit) {
      try {
        this.musicKit.unauthorize();
      } catch (e) {
        // Ignore errors during cleanup
      }
    }
  }

  /**
   * Authorize user (opens Apple Music login)
   */
  public static async authorize(): Promise<boolean> {
    await this.initialize();

    // Check if already authorized - don't force re-auth
    if (this.musicKit?.isAuthorized) {
      this.userToken = this.musicKit.musicUserToken;
      if (this.userToken) {
        localStorage.setItem('apple_music_user_token', this.userToken);
      }
      return true;
    }

    try {
      const userToken = await this.musicKit.authorize();
      this.userToken = userToken;

      // Store the token
      localStorage.setItem('apple_music_user_token', userToken);

      return true;
    } catch (error) {
      console.error('[AppleMusic] Authorization failed:', error);
      // Only clear state on actual failure
      localStorage.removeItem('apple_music_user_token');
      this.userToken = null;
      return false;
    }
  }

  /**
   * Unauthorize user
   */
  public static async unauthorize(): Promise<void> {
    if (this.musicKit) {
      await this.musicKit.unauthorize();
    }

    this.userToken = null;
    localStorage.removeItem('apple_music_user_token');
  }

  /**
   * Make an authenticated API call to Apple Music
   * Uses stored tokens directly for Electron IPC auth compatibility
   */
  private static async apiCall(
    endpoint: string,
    params?: Record<string, any>,
  ): Promise<any> {
    // First, try using MusicKit if it's authorized
    if (this.musicKit?.isAuthorized) {
      try {
        return await this.musicKit.api.music(endpoint, params);
      } catch (error: any) {
        // If MusicKit fails with auth error, fall through to direct API call
        if (error?.name !== 'ACCESS_DENIED') {
          throw error;
        }
        console.log('[AppleMusic] MusicKit auth failed, trying direct API');
      }
    }

    // Fall back to direct API call with stored tokens
    if (!this.userToken || !this.developerToken) {
      throw new Error('Not authorized with Apple Music');
    }

    const url = new URL(`https://api.music.apple.com${endpoint}`);
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        url.searchParams.set(key, String(value));
      });
    }

    const response = await fetch(url.toString(), {
      headers: {
        Authorization: `Bearer ${this.developerToken}`,
        'Music-User-Token': this.userToken,
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Apple Music API error: ${response.status} ${errorText}`);
    }

    return { data: await response.json() };
  }

  /**
   * Get user's playlists
   */
  public static async getPlaylists(): Promise<AppleMusicPlaylist[]> {
    await this.initialize();

    if (!this.isAuthorized()) {
      throw new Error('Not authorized with Apple Music');
    }

    try {
      const response = await this.apiCall('/v1/me/library/playlists', {
        limit: 100,
      });

      const playlists =
        response.data?.data?.map((p: any) => ({
          id: p.id,
          name: p.attributes?.name || 'Untitled',
          description: p.attributes?.description?.standard,
          trackCount: p.attributes?.trackCount || 0,
          image: p.attributes?.artwork?.url
            ?.replace('{w}', '300')
            .replace('{h}', '300'),
        })) || [];

      return playlists;
    } catch (error) {
      console.error('[AppleMusic] Failed to get playlists:', error);
      throw error;
    }
  }

  /**
   * Get playlist details including track count
   */
  public static async getPlaylist(playlistId: string): Promise<{
    id: string;
    attributes?: { trackCount?: number };
    relationships?: { tracks?: { data?: any[] } };
  } | null> {
    await this.initialize();

    if (!this.isAuthorized()) {
      return null;
    }

    try {
      const response = await this.apiCall(
        `/v1/me/library/playlists/${playlistId}`,
        { include: 'tracks' },
      );
      return response?.data?.data?.[0] || null;
    } catch (error) {
      console.error('[AppleMusic] Error fetching playlist:', error);
      return null;
    }
  }

  /**
   * Get tracks from a playlist
   */
  public static async getPlaylistTracks(playlistId: string): Promise<iTrack[]> {
    await this.initialize();

    if (!this.isAuthorized()) {
      throw new Error('Not authorized with Apple Music');
    }

    try {
      // First, try to get the playlist with its tracks included
      const response = await this.apiCall(
        `/v1/me/library/playlists/${playlistId}`,
        { include: 'tracks' },
      );

      console.log('[AppleMusic] Playlist response structure:', {
        hasData: !!response.data,
        dataType: typeof response.data,
        topLevelKeys: response.data ? Object.keys(response.data) : [],
      });

      // Try different paths where tracks might be
      let tracks: any[] = [];

      // Path 1: Included tracks in relationships
      if (response.data?.data?.[0]?.relationships?.tracks?.data) {
        tracks = response.data.data[0].relationships.tracks.data;
        console.log(
          `[AppleMusic] Found ${tracks.length} tracks in relationships.tracks.data`,
        );
      }
      // Path 2: Direct data array
      else if (response.data?.data) {
        // If we got the playlist, try fetching tracks separately
        const tracksResponse = await this.apiCall(
          `/v1/me/library/playlists/${playlistId}/tracks`,
          { limit: 300 },
        );

        console.log('[AppleMusic] Tracks response structure:', {
          hasData: !!tracksResponse.data,
          dataKeys: tracksResponse.data ? Object.keys(tracksResponse.data) : [],
        });

        if (tracksResponse.data?.data) {
          tracks = tracksResponse.data.data;
          console.log(
            `[AppleMusic] Found ${tracks.length} tracks in separate tracks request`,
          );
        }
      }

      if (tracks.length === 0) {
        console.log(
          '[AppleMusic] No tracks found. Full response:',
          JSON.stringify(response.data, null, 2).slice(0, 1000),
        );
      } else {
        // Log first track structure for debugging
        console.log(
          '[AppleMusic] First track structure:',
          JSON.stringify(tracks[0], null, 2).slice(0, 500),
        );
      }

      return tracks.map((t: any) => this.mapToITrack(t));
    } catch (error) {
      console.error('[AppleMusic] Failed to get playlist tracks:', error);
      throw error;
    }
  }

  /**
   * Search for tracks in the Apple Music catalog
   */
  public static async searchTracks(query: string): Promise<iTrack[]> {
    await this.initialize();

    try {
      // Get the storefront (region) dynamically
      const storefront = this.musicKit?.storefrontId || 'us';

      const response = await this.musicKit.api.music(
        `/v1/catalog/${storefront}/search`,
        {
          term: query,
          types: 'songs',
          limit: 10,
        },
      );

      const songs = response.data?.results?.songs?.data || [];

      // Debug: Log search results
      if (songs.length > 0) {
        console.log(
          `[AppleMusic] ✓ Search "${query.slice(0, 40)}..." found ${songs.length} results, first: "${songs[0].attributes?.name}"`,
        );
      } else {
        console.log(
          `[AppleMusic] ✗ Search "${query.slice(0, 40)}..." - no results`,
        );
      }

      return songs.map((t: any) => this.mapToITrack(t));
    } catch (error) {
      console.error('[AppleMusic] Search failed:', query, error);
      return [];
    }
  }

  /**
   * Search by ISRC
   */
  public static async searchByIsrc(isrc: string): Promise<iTrack[]> {
    await this.initialize();

    try {
      // Get the storefront (region) dynamically
      const storefront = this.musicKit?.storefrontId || 'us';

      const response = await this.musicKit.api.music(
        `/v1/catalog/${storefront}/songs`,
        {
          'filter[isrc]': isrc,
        },
      );

      const songs = response.data?.data || [];

      // Debug: Log ISRC lookup results
      if (songs.length === 0) {
        console.log(`[AppleMusic] ✗ ISRC not found: ${isrc}`);
      } else {
        console.log(
          `[AppleMusic] ✓ ISRC ${isrc} found: "${songs[0].attributes?.name}" by "${songs[0].attributes?.artistName}"`,
        );
      }

      return songs.map((t: any) => this.mapToITrack(t));
    } catch (error) {
      console.error('[AppleMusic] ISRC lookup failed:', isrc, error);
      return [];
    }
  }

  /**
   * Create a playlist using direct API call (bypasses MusicKit auth issues)
   */
  public static async createPlaylist(
    name: string,
    description?: string,
  ): Promise<string | null> {
    await this.initialize();

    // Get tokens - prefer stored tokens for Electron compatibility
    const userToken =
      this.userToken || localStorage.getItem('apple_music_user_token');
    const devToken =
      this.developerToken || sessionStorage.getItem('apple_music_dev_token');

    console.log('[AppleMusic] Creating playlist:', name);
    console.log('[AppleMusic] User token available:', !!userToken);
    console.log('[AppleMusic] Dev token available:', !!devToken);

    if (!userToken || !devToken) {
      throw new Error('Not authorized with Apple Music');
    }

    try {
      // Use direct fetch to ensure tokens are properly passed
      const response = await fetch(
        'https://api.music.apple.com/v1/me/library/playlists',
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${devToken}`,
            'Music-User-Token': userToken,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            attributes: {
              name,
              description: description || 'Created by Crate Hackers',
            },
          }),
        },
      );

      console.log(
        '[AppleMusic] Create playlist response status:',
        response.status,
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error(
          '[AppleMusic] Create playlist error:',
          response.status,
          errorText,
        );

        // If 401 or 403, token is expired/invalid - clear it so user can re-auth
        if (response.status === 401 || response.status === 403) {
          console.log(
            '[AppleMusic] Token expired or invalid - clearing tokens',
          );
          this.clearTokens();
          throw new Error(
            'Apple Music session expired. Please reconnect to Apple Music.',
          );
        }

        throw new Error(
          `Apple Music API error: ${response.status} ${errorText}`,
        );
      }

      const data = await response.json();
      console.log(
        '[AppleMusic] Create playlist response:',
        JSON.stringify(data, null, 2),
      );

      const playlistId = data?.data?.[0]?.id;
      console.log('[AppleMusic] Extracted playlist ID:', playlistId);

      if (!playlistId) {
        console.error(
          '[AppleMusic] No playlist ID in response. Full response:',
          data,
        );
      }

      return playlistId || null;
    } catch (error) {
      console.error('[AppleMusic] Failed to create playlist:', error);
      throw error; // Re-throw so caller knows it failed
    }
  }

  /**
   * Add tracks to a playlist using direct API call (bypasses MusicKit auth issues)
   */
  public static async addTracksToPlaylist(
    playlistId: string,
    trackIds: string[],
  ): Promise<boolean> {
    await this.initialize();

    // Get tokens - prefer stored tokens for Electron compatibility
    const userToken =
      this.userToken || localStorage.getItem('apple_music_user_token');
    const devToken =
      this.developerToken || sessionStorage.getItem('apple_music_dev_token');

    if (!userToken || !devToken) {
      throw new Error('Not authorized with Apple Music');
    }

    try {
      console.log(
        `[AppleMusic] Adding ${trackIds.length} tracks to playlist ${playlistId}`,
      );
      console.log('[AppleMusic] First few track IDs:', trackIds.slice(0, 5));

      // Apple Music accepts up to 100 tracks per request
      const chunkSize = 100;

      for (let i = 0; i < trackIds.length; i += chunkSize) {
        const batch = trackIds.slice(i, i + chunkSize);
        console.log(
          `[AppleMusic] Adding batch ${Math.floor(i / chunkSize) + 1}/${Math.ceil(trackIds.length / chunkSize)}`,
        );

        // For library playlists, we need to use library song IDs or catalog IDs
        const tracks = batch.map((id) => ({
          id,
          type: id.startsWith('i.') ? 'library-songs' : 'songs',
        }));

        console.log(
          '[AppleMusic] Track payload sample:',
          JSON.stringify(tracks.slice(0, 3)),
        );

        // Use direct fetch to ensure tokens are properly passed
        const response = await fetch(
          `https://api.music.apple.com/v1/me/library/playlists/${playlistId}/tracks`,
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${devToken}`,
              'Music-User-Token': userToken,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ data: tracks }),
          },
        );

        console.log(
          '[AppleMusic] Add tracks response status:',
          response.status,
        );

        if (!response.ok) {
          const errorText = await response.text();
          console.error(
            '[AppleMusic] Add tracks error:',
            response.status,
            errorText,
          );

          // If 401 or 403, token is expired/invalid - clear it so user can re-auth
          if (response.status === 401 || response.status === 403) {
            console.log(
              '[AppleMusic] Token expired or invalid - clearing tokens',
            );
            this.clearTokens();
            throw new Error(
              'Apple Music session expired. Please reconnect to Apple Music.',
            );
          }

          throw new Error(
            `Apple Music API error: ${response.status} ${errorText}`,
          );
        }

        // Response might be empty for successful adds
        const responseText = await response.text();
        if (responseText) {
          console.log('[AppleMusic] Add tracks response:', responseText);
        }
      }

      return true;
    } catch (error) {
      console.error('[AppleMusic] Failed to add tracks:', error);
      throw error; // Re-throw so caller knows it failed
    }
  }

  /**
   * Map Apple Music track to iTrack
   * Handles both catalog tracks and library tracks which have slightly different structures
   */
  private static mapToITrack(item: any): iTrack {
    const attrs = item.attributes || {};

    // Handle version info similar to Tidal
    const title = attrs.name || '';

    // For library tracks, the playParams might contain the catalog ID
    const catalogId = attrs.playParams?.catalogId || item.id;

    // Duration might be in different fields depending on track type
    const duration =
      attrs.durationInMillis ||
      (attrs.duration ? attrs.duration * 1000 : undefined);

    return {
      ID: catalogId, // Use catalog ID for matching if available
      Artist: attrs.artistName || 'Unknown Artist',
      Title: title,
      Album: attrs.albumName,
      AlbumCover: attrs.artwork?.url
        ?.replace('{w}', '300')
        .replace('{h}', '300'),
      duration: duration,
      isrc: attrs.isrc, // Library tracks might not have ISRC
      BPM: undefined,
      Key_Camelot: undefined,
      PreviewURL: attrs.previews?.[0]?.url,
    };
  }

  /**
   * Get storefront (region)
   */
  public static async getStorefront(): Promise<string> {
    await this.initialize();

    try {
      const storefront = await this.musicKit.storefrontId;
      return storefront || 'us';
    } catch {
      return 'us';
    }
  }
}

export default AppleMusicServiceClass;
